import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, HOME_PAGE_EVENT } from '../analytics/posthog';
import { useContentCatalog } from '../utils/contentCatalog';
import styles from './Welcome.module.css';
import BrandLogo from './BrandLogo';
import logoImage from '../assets/logo.png';
import landscapeImage from '../assets/landscape.png';
import iconBudget from '../assets/welcome/welcome-icon-budget.png';
import iconTarget from '../assets/welcome/welcome-icon-target.png';
import iconShield from '../assets/welcome/welcome-icon-shield.png';

const LEARN_MORE_URL = 'https://www.richuel.com';
const PARALLAX_ARM_MS = 1100;
const PARALLAX_SHIFT_X = 14;
const PARALLAX_SHIFT_Y = 10;
const PARALLAX_BADGE_FACTOR = 0.35;
const PARALLAX_IMAGE_SCALE = 1.07;

const WAVE_DESKTOP = {
  viewBox: '0 0 240 1000',
  d: 'M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z',
  dur: '14s',
  values:
    'M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z;M0 0 H150 C150 90 58 168 70 310 C84 452 196 518 186 658 C176 798 86 862 112 1000 H0 Z;M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z',
};

const WAVE_SHEET = {
  viewBox: '0 0 576 1024',
  d: 'M 0 210 C 6 211.7, 24 215.3, 36 220 C 48 224.7, 60 229.3, 72 238 C 84 246.7, 96 259.3, 108 272 C 120 284.7, 132 303.3, 144 314 C 156 324.7, 168 330.7, 180 336 C 192 341.3, 202 343.3, 216 346 C 230 348.7, 248 349.7, 264 352 C 280 354.3, 296 356.5, 312 360 C 328 363.5, 344 368.3, 360 373 C 376 377.7, 392 381.2, 408 388 C 424 394.8, 440 403.3, 456 414 C 472 424.7, 490 442, 504 452 C 518 462, 528 467.3, 540 474 C 552 480.7, 570 489, 576 492 L 576 1024 L 0 1024 Z',
};

const WELCOME_FEATURE_ART = {
  runway: { className: 'featureCardBudget', icon: iconBudget },
  plan: { className: 'featureCardPlan', icon: iconTarget },
  confidence: { className: 'featureCardConfidence', icon: iconShield },
};

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useHeroParallax(enabled) {
  const heroRef = useRef(null);
  const imageRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const hero = heroRef.current;
    const img = imageRef.current;
    const badge = badgeRef.current;
    if (!hero || !img) {
      return undefined;
    }

    let armed = false;
    let raf = 0;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, PARALLAX_ARM_MS);

    const reset = () => {
      img.style.transform = '';
      if (badge) {
        badge.style.transform = '';
      }
    };

    const onMove = (event) => {
      if (!armed) {
        return;
      }
      const bounds = hero.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) {
        return;
      }
      const nx = (event.clientX - bounds.left) / bounds.width - 0.5;
      const ny = (event.clientY - bounds.top) / bounds.height - 0.5;
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const tx = nx * PARALLAX_SHIFT_X;
        const ty = ny * PARALLAX_SHIFT_Y;
        img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${PARALLAX_IMAGE_SCALE})`;
        if (badge) {
          badge.style.transform = `translate3d(${-tx * PARALLAX_BADGE_FACTOR}px, ${-ty * PARALLAX_BADGE_FACTOR}px, 0)`;
        }
      });
    };

    hero.addEventListener('pointermove', onMove, { passive: true });
    hero.addEventListener('pointerleave', reset);
    hero.addEventListener('pointercancel', reset);

    return () => {
      window.clearTimeout(armTimer);
      cancelAnimationFrame(raf);
      hero.removeEventListener('pointermove', onMove);
      hero.removeEventListener('pointerleave', reset);
      hero.removeEventListener('pointercancel', reset);
      reset();
    };
  }, [enabled]);

  return { heroRef, imageRef, badgeRef };
}

const LockIcon = () => (
  <svg className={styles.betaLock} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="5.5" y="11" width="13" height="9.5" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8.2 11V8.4a3.8 3.8 0 0 1 7.6 0V11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const SecurityShieldIcon = () => (
  <svg className={styles.securityShield} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 3l7 4v6c0 5-3.5 8.5-7 9.5-3.5-1-7-4.5-7-9.5V7l7-4z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      className={styles.securityCheck}
      d="M8.4 12.15l2.4 2.4 4.8-5.1"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeadlineUnderline = () => (
  <svg className={styles.headlineUnderline} viewBox="0 0 240 18" preserveAspectRatio="none" aria-hidden>
    <path
      className={styles.headlineUnderlineGold}
      d="M4 7.2 C52 3.6, 98 11.4, 146 7.1 S206 3.8, 236 7.6"
      stroke="#C9A227"
      strokeWidth="3.1"
      fill="none"
      strokeLinecap="round"
    />
    <path
      className={styles.headlineUnderlineNavy}
      d="M5 12.6 C54 9.2, 100 15.4, 148 11.6 S207 8.6, 235 12.8"
      stroke="#044766"
      strokeWidth="1.45"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

function DecorativeWave({ className, wave, motionEnhanced }) {
  return (
    <svg className={className} viewBox={wave.viewBox} preserveAspectRatio="none" aria-hidden focusable="false">
      <path d={wave.d}>
        {motionEnhanced ? (
          <animate attributeName="d" dur={wave.dur} repeatCount="indefinite" values={wave.values} />
        ) : null}
      </path>
    </svg>
  );
}

function FeatureItem({ card, className }) {
  return (
    <div className={className}>
      <span className={styles.featureIconWrap} aria-hidden>
        <img src={card.icon} alt="" className={styles.featureIcon} decoding="async" />
      </span>
      <span>{card.label}</span>
    </div>
  );
}

function AuthButtons({ copy, onLogin, onSignup }) {
  return (
    <>
      <button type="button" className={styles.authLogin} onClick={onLogin} aria-label={copy.log_in_aria}>
        {copy.log_in}
      </button>
      <button type="button" className={styles.authSignup} onClick={onSignup} aria-label={copy.sign_up_aria}>
        {copy.sign_up}
      </button>
    </>
  );
}

function TrustBadge({ icon, text, className, badgeRef }) {
  return (
    <div ref={badgeRef} className={className ? `${styles.betaBadge} ${className}` : styles.betaBadge}>
      <span className={styles.betaIcon} aria-hidden>
        {icon}
      </span>
      <p className={styles.betaText}>{text}</p>
    </div>
  );
}

const Welcome = () => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const copy = catalog?.marketing?.welcome;
  const featureCards = useMemo(
    () => (copy?.features || []).map((feature) => ({
      ...feature,
      ...(WELCOME_FEATURE_ART[feature.id] || {}),
    })).filter((card) => card.icon),
    [copy?.features],
  );
  const posthog = usePostHog();
  const motionEnhanced = !prefersReducedMotion();
  const { heroRef, imageRef, badgeRef } = useHeroParallax(motionEnhanced);

  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.clear();
  }, []);

  const trackWelcome = useCallback(
    (action) => capture(posthog, HOME_PAGE_EVENT, { page: 'welcome', action }),
    [posthog]
  );

  const handleLogin = useCallback(() => {
    trackWelcome('log_in');
    navigate('/login');
  }, [navigate, trackWelcome]);

  const handleSignup = useCallback(() => {
    trackWelcome('sign_up');
    navigate('/signup');
  }, [navigate, trackWelcome]);

  const handleStartJourney = useCallback(() => {
    trackWelcome('start_journey');
    navigate('/signup');
  }, [navigate, trackWelcome]);

  const handleLearnMore = useCallback(() => {
    trackWelcome('learn_more');
    window.open(LEARN_MORE_URL, '_blank');
  }, [trackWelcome]);

  const authButtons = (
    <AuthButtons copy={copy || {}} onLogin={handleLogin} onSignup={handleSignup} />
  );

  return (
    <div className={`${styles.welcomePage} ${motionEnhanced ? styles.welcomeSmooth : ''}`}>
      <main className={styles.welcomeMain} id="welcome-main" aria-label="Welcome">
        <section ref={heroRef} className={styles.heroCard} aria-labelledby="welcome-headline">
          <div className={styles.copyCol}>
            <div className={styles.brandRow}>
              <div className={styles.brand}>
                <BrandLogo src={logoImage} alt="Richuel" decoding="async" />
              </div>
              <div className={styles.copyAuth}>{authButtons}</div>
            </div>

            <div className={styles.copyBlock}>
              <h1 id="welcome-headline" className={styles.headline}>
                {copy?.headline_before}{' '}
                <span className={styles.headlineAccent}>
                  {copy?.headline_accent}
                  <HeadlineUnderline />
                </span>
              </h1>
              <p className={styles.subcopy}>{copy?.subcopy}</p>
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.cta}
                  onClick={handleStartJourney}
                  aria-label={copy?.start_journey_aria}
                >
                  <span>{copy?.start_journey}</span>
                  <span className={styles.ctaArrows} aria-hidden>
                    {'>>'}
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.secondaryCta}
                  onClick={handleLearnMore}
                  aria-label={copy?.learn_more_aria}
                >
                  {copy?.learn_more}
                </button>
              </div>
            </div>

            <div className={styles.featureStack}>
              {featureCards.map((card) => (
                <FeatureItem key={card.id} card={card} className={styles.featureRow} />
              ))}
            </div>

            <div className={styles.copyFooter}>
              <TrustBadge icon={<LockIcon />} text={copy?.beta_notice} badgeRef={badgeRef} />
              <TrustBadge icon={<SecurityShieldIcon />} text={copy?.security_badge} className={styles.securityBadge} />
            </div>
          </div>

          <div className={styles.visualCol}>
            <div className={styles.imageClip}>
              <img
                ref={imageRef}
                src={landscapeImage}
                alt=""
                className={styles.heroImg}
                decoding="async"
                fetchPriority="high"
              />
            </div>

            <DecorativeWave className={styles.wave} wave={WAVE_DESKTOP} motionEnhanced={motionEnhanced} />

            <div className={styles.authBar}>{authButtons}</div>

            {featureCards.map((card) => (
              <FeatureItem
                key={card.id}
                card={card}
                className={`${styles.featureCard} ${styles[card.className]}`}
              />
            ))}
          </div>

          <DecorativeWave className={styles.sheet} wave={WAVE_SHEET} motionEnhanced={false} />
        </section>
      </main>
    </div>
  );
};

export default Welcome;
